import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Empty,
  Input,
  message,
  Progress,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CloudOutlined,
  DatabaseOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { UserContext } from 'services/contexts';
import {
  useGetAnnotationSetsQuery,
  useLazyGetReviewFramesQuery,
  useLazyGetReviewFramesBySegmentationUpdatedAtQuery,
  useUpdateReviewFrameMutation,
} from '../../redux/slices/reviews';
import {
  useGetReviewFramePreviewQuery,
  usePrefetchReviewFramePreview,
} from '../../redux/slices/views';
import { AnnotationSet, FrameReviewDecision, ReviewFrame } from 'models/reviews';
import { Frame, SegmentationLabel } from 'models/segmentation';
import SegICIDOverlay from 'components/SegmentationLabels/components/SegICIDOveraly';
import {
  ActionDock,
  ActionGroup,
  CacheNotice,
  Eyebrow,
  FieldLabel,
  FrameMeta,
  ImageStage,
  Legend,
  LegendItem,
  MetaStatus,
  Page,
  PreviewCard,
  PreviewGrid,
  PreviewHeading,
  PreviewImage,
  ProgressBlock,
  ReviewComment,
  SetupCard,
  SetupHint,
  SetupShell,
  SummaryCard,
  SummaryHeader,
  Timer,
  Toolbar,
  ToolbarTitle,
  Workspace,
} from './styled';

const { Paragraph, Text, Title } = Typography;
const CACHE_PREFIX = 'reviewer-frame-review:v1';
const PREVIEW_BUFFER_SIZE = 10;
const FRAME_REVIEW_DECISIONS: FrameReviewDecision[] = ['DISMISSED', 'MISSING', 'SWAP', 'REDO'];

interface CachedChoice {
  reviewFrameUuid: string;
  dicomId: number;
  dicomIcid: string;
  frameIndex: number;
  decision: FrameReviewDecision;
  features: string[];
  reviewedAt: string;
}

interface SummaryRow extends Omit<CachedChoice, 'decision'> {
  queueIndex: number;
  decision?: FrameReviewDecision;
}

interface CachedSession {
  version: 1;
  annotationSet: Pick<AnnotationSet, 'uuid' | 'icid' | 'name'>;
  currentIndex: number;
  total: number;
  startedAt: string;
  choices: Record<string, CachedChoice>;
  comments?: Record<string, string>;
  savedChoices: Record<string, FrameReviewDecision>;
  savedComments?: Record<string, string>;
  lastSavedAt?: string;
  sourceType?: 'annotation-set' | 'updated-at';
  segmentationUpdatedAtAfter?: string;
  segmentationUpdatedAtBefore?: string;
}

interface UpdatedAtRange {
  after: string;
  before: string;
}

interface PendingReviewFrameUpdate {
  reviewFrameUuid: string;
  dicomId: number;
  frameIndex: number;
  comment?: string;
  decision?: FrameReviewDecision;
  status?: 'IN_REVIEW' | 'DISMISSED';
}

interface FramePreviewProps {
  frame: Frame;
  overlay?: boolean;
}

const readSession = (key: string): CachedSession | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedSession;
    if (parsed.version !== 1 || !parsed.annotationSet?.uuid || !parsed.choices) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSession = (key: string, session: CachedSession) => {
  try {
    localStorage.setItem(key, JSON.stringify(session));
  } catch {
    message.warning('This review is open, but browser storage is unavailable.');
  }
};

const reviewFrameKey = (frame: ReviewFrame) =>
  frame.queueKey || `${frame.dicomId}:${frame.frameIndex}`;

const reconcileCachedSession = (cached: CachedSession, frames: ReviewFrame[]): CachedSession => {
  const choices: Record<string, CachedChoice> = {};
  const comments: Record<string, string> = {};
  const savedChoices: Record<string, FrameReviewDecision> = {};
  const savedComments: Record<string, string> = {};

  frames.forEach(frame => {
    const key = reviewFrameKey(frame);
    const legacyKey = frame.uuid || '';
    const choice = cached.choices[key] || cached.choices[legacyKey];
    const comment = cached.comments?.[key] ?? cached.comments?.[legacyKey];
    const savedChoice = cached.savedChoices?.[key] || cached.savedChoices?.[legacyKey];
    const savedComment = cached.savedComments?.[key] ?? cached.savedComments?.[legacyKey];
    if (choice) choices[key] = { ...choice, reviewFrameUuid: key };
    if (comment !== undefined) comments[key] = comment;
    if (savedChoice) savedChoices[key] = savedChoice;
    if (savedComment !== undefined) savedComments[key] = savedComment;
  });

  return { ...cached, choices, comments, savedChoices, savedComments };
};

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const twoDigits = (value: number) => String(value).padStart(2, '0');
  return hours > 0
    ? `${hours}:${twoDigits(minutes)}:${twoDigits(seconds)}`
    : `${twoDigits(minutes)}:${twoDigits(seconds)}`;
};

const decisionLabel = (decision: FrameReviewDecision) => {
  const labels: Record<FrameReviewDecision, string> = {
    DISMISSED: 'Skip',
    MISSING: 'Missing',
    SWAP: 'Change class',
    REDO: 'Redo',
  };
  return labels[decision];
};

const decisionColor = (decision: FrameReviewDecision) => {
  const colors: Record<FrameReviewDecision, string> = {
    DISMISSED: 'default',
    MISSING: 'orange',
    SWAP: 'purple',
    REDO: 'red',
  };
  return colors[decision];
};

const asFrameReviewDecision = (decision?: string | null): FrameReviewDecision | undefined =>
  FRAME_REVIEW_DECISIONS.find(candidate => candidate === decision);

const selectedDecisionStyle = (decision: FrameReviewDecision, selected: boolean) => {
  if (!selected) return undefined;
  const colors: Record<FrameReviewDecision, string> = {
    DISMISSED: '#475467',
    MISSING: '#d97706',
    SWAP: '#722ed1',
    REDO: '#cf1322',
  };
  return {
    background: colors[decision],
    borderColor: colors[decision],
    color: '#fff',
  };
};

const FramePreview: React.FC<FramePreviewProps> = ({ frame, overlay = false }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [frameDimensions, setFrameDimensions] = useState({ width: 0, height: 0 });
  const segmentations = frame.segmentationLabels || [];
  const visibleSegmentations = useMemo(
    () => new Set(segmentations.map(segmentation => segmentation.icid)),
    [segmentations]
  );

  return (
    <PreviewCard>
      <PreviewHeading>
        <span>{overlay ? 'Segmentation overlay' : 'Raw image'}</span>
        {overlay && <Text type="secondary">{segmentations.length} labels</Text>}
      </PreviewHeading>
      <ImageStage ref={wrapperRef}>
        <PreviewImage
          src={frame.fileUrl}
          alt={overlay ? 'Frame with segmentation overlay' : 'Original frame'}
          fetchPriority="high"
          decoding="async"
          onLoad={event => {
            const image = event.currentTarget;
            setFrameDimensions({ width: image.naturalWidth, height: image.naturalHeight });
          }}
        />
        {overlay && (
          <SegICIDOverlay
            frameDimensions={frameDimensions}
            allSegmentations={segmentations}
            visibleSegmentations={visibleSegmentations}
            wrapperRef={wrapperRef}
          />
        )}
      </ImageStage>
      {overlay && (
        <Legend aria-label="Segmentation legend">
          {segmentations.length > 0 ? (
            segmentations.map(segmentation => (
              <LegendItem
                key={segmentation.uuid}
                $color={segmentation.segmentationColor || '#1890ff'}
              >
                {segmentation.segmentationClassValue || 'Unknown feature'}
              </LegendItem>
            ))
          ) : (
            <Text type="secondary">No segmentation labels on this frame</Text>
          )}
        </Legend>
      )}
    </PreviewCard>
  );
};

const FrameReview = () => {
  const { user } = useContext(UserContext);
  const {
    data: annotationSets = [],
    isLoading: setsAreLoading,
    error: setsError,
  } = useGetAnnotationSetsQuery();
  const [loadReviewFrames, { isFetching: framesAreLoading }] = useLazyGetReviewFramesQuery();
  const [loadUpdatedAtReviewFrames, { isFetching: updatedAtFramesAreLoading }] =
    useLazyGetReviewFramesBySegmentationUpdatedAtQuery();
  const [updateReviewFrame] = useUpdateReviewFrameMutation();

  const [selectedSetId, setSelectedSetId] = useState<string>();
  const userCacheIdentity = user.uid || user.email || 'anonymous';
  const [reviewSource, setReviewSource] = useState<'annotation-set' | 'updated-at'>(
    'annotation-set'
  );
  const [updatedAtRange, setUpdatedAtRange] = useState<UpdatedAtRange>();
  const [activeSet, setActiveSet] = useState<AnnotationSet>();
  const [reviewFrames, setReviewFrames] = useState<ReviewFrame[]>([]);
  const [session, setSession] = useState<CachedSession>();
  const [view, setView] = useState<'setup' | 'review' | 'summary'>('setup');
  const [isSaving, setIsSaving] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(20);
  const prefetchReviewFramePreview = usePrefetchReviewFramePreview();
  const prefetchedPreviewKeys = useRef(new Set<string>());
  const preloadedPreviewImages = useRef(new Map<string, HTMLImageElement>());

  const cacheKey = useCallback(
    (annotationSetId: string) => `${CACHE_PREFIX}:${userCacheIdentity}:${annotationSetId}`,
    [userCacheIdentity]
  );

  const selectedSet = useMemo(
    () => annotationSets.find(annotationSet => annotationSet.uuid === selectedSetId),
    [annotationSets, selectedSetId]
  );
  const updatedAtSourceId = updatedAtRange
    ? `updated-at:${updatedAtRange.after}:${updatedAtRange.before}`
    : undefined;
  const selectedSourceId = reviewSource === 'updated-at' ? updatedAtSourceId : selectedSetId;
  const resumableSession = useMemo(
    () => (selectedSourceId ? readSession(cacheKey(selectedSourceId)) : null),
    [cacheKey, selectedSourceId, view]
  );
  const currentIndex = session?.currentIndex || 0;
  const activeReviewFrame = reviewFrames[currentIndex];
  const activeReviewFrameKey = activeReviewFrame ? reviewFrameKey(activeReviewFrame) : undefined;
  const activeChoice = activeReviewFrameKey ? session?.choices[activeReviewFrameKey] : undefined;
  const activeComment = activeReviewFrameKey
    ? (session?.comments?.[activeReviewFrameKey] ?? activeReviewFrame?.comment ?? '')
    : '';
  const persistedDecision = asFrameReviewDecision(activeReviewFrame?.decision);
  const effectiveDecision = activeChoice?.decision || persistedDecision;
  const localChoiceIsSaved = Boolean(
    activeChoice &&
      (session?.savedChoices[activeChoice.reviewFrameUuid] === activeChoice.decision ||
        persistedDecision === activeChoice.decision)
  );
  const previewBuffer = useMemo(
    () => reviewFrames.slice(currentIndex + 1, currentIndex + 1 + PREVIEW_BUFFER_SIZE),
    [currentIndex, reviewFrames]
  );

  const {
    currentData: currentPreviewPage,
    isFetching: previewIsFetching,
    error: previewError,
  } = useGetReviewFramePreviewQuery(
    {
      dicomIcid: activeReviewFrame?.dicomIcid || '',
      frameIndex: activeReviewFrame?.frameIndex || 0,
      includeEmptyFrame: false,
    },
    { skip: view !== 'review' || !activeReviewFrame }
  );
  const activeFrame = currentPreviewPage?.results?.[0];
  const scopedActiveFrame = useMemo(() => {
    if (!activeFrame || session?.sourceType === 'updated-at' || !activeReviewFrame?.userEmail)
      return activeFrame;
    const reviewUserEmail = activeReviewFrame.userEmail.toLowerCase();
    return {
      ...activeFrame,
      segmentationLabels: (activeFrame.segmentationLabels || []).filter(
        segmentation => segmentation.userEmail?.toLowerCase() === reviewUserEmail
      ),
    };
  }, [activeFrame, activeReviewFrame?.userEmail, session?.sourceType]);
  const previewIsPending = previewIsFetching && !scopedActiveFrame;
  useEffect(() => {
    if (view !== 'review') return;

    previewBuffer.forEach(frame => {
      const key = reviewFrameKey(frame);
      if (prefetchedPreviewKeys.current.has(key)) return;
      prefetchedPreviewKeys.current.add(key);

      void prefetchReviewFramePreview({
        dicomIcid: frame.dicomIcid,
        frameIndex: frame.frameIndex,
        includeEmptyFrame: false,
      })
        .then(page => {
          const fileUrl = page.results?.[0]?.fileUrl;
          if (!fileUrl) return;

          const image = new Image();
          image.decoding = 'async';
          image.setAttribute('fetchpriority', 'low');
          image.onload = image.onerror = () => preloadedPreviewImages.current.delete(key);
          preloadedPreviewImages.current.set(key, image);
          image.src = fileUrl;
        })
        .catch(() => {
          prefetchedPreviewKeys.current.delete(key);
        });
    });
  }, [prefetchReviewFramePreview, previewBuffer, view]);

  const reviewedCount = session
    ? reviewFrames.filter(
        frame => session.choices[reviewFrameKey(frame)] || asFrameReviewDecision(frame.decision)
      ).length
    : 0;
  const total = reviewFrames.length;
  const progress = total > 0 ? Math.round((reviewedCount / total) * 100) : 0;
  const dirtyUpdates = useMemo(() => {
    if (!session) return [];
    return reviewFrames.reduce<PendingReviewFrameUpdate[]>((updates, frame) => {
      const key = reviewFrameKey(frame);
      const choice = session.choices[key];
      const savedDecision = session.savedChoices[key] || asFrameReviewDecision(frame.decision);
      const decisionIsDirty = Boolean(choice && choice.decision !== savedDecision);
      const effectiveFrameDecision = choice?.decision || savedDecision;
      const hasLocalComment = Object.prototype.hasOwnProperty.call(session.comments || {}, key);
      const savedComment = Object.prototype.hasOwnProperty.call(session.savedComments || {}, key)
        ? session.savedComments?.[key] || ''
        : frame.comment || '';
      const localComment = session.comments?.[key] || '';
      const commentIsDirty = hasLocalComment && localComment !== savedComment;

      if (decisionIsDirty || (commentIsDirty && effectiveFrameDecision)) {
        updates.push({
          reviewFrameUuid: key,
          dicomId: frame.dicomId,
          frameIndex: frame.frameIndex,
          ...(commentIsDirty && effectiveFrameDecision ? { comment: localComment } : {}),
          ...(decisionIsDirty && choice
            ? {
                decision: choice.decision,
                status: choice.decision === 'DISMISSED' ? 'DISMISSED' : 'IN_REVIEW',
              }
            : {}),
        });
      }
      return updates;
    }, []);
  }, [reviewFrames, session]);
  const commentsWithoutDecisions = useMemo(() => {
    if (!session) return [];
    return reviewFrames.filter(frame => {
      const key = reviewFrameKey(frame);
      const hasLocalComment = Object.prototype.hasOwnProperty.call(session.comments || {}, key);
      if (!hasLocalComment) return false;
      const savedComment = Object.prototype.hasOwnProperty.call(session.savedComments || {}, key)
        ? session.savedComments?.[key] || ''
        : frame.comment || '';
      const commentIsDirty = (session.comments?.[key] || '') !== savedComment;
      const decision =
        session.choices[key]?.decision ||
        session.savedChoices[key] ||
        asFrameReviewDecision(frame.decision);
      return commentIsDirty && !decision;
    });
  }, [reviewFrames, session]);
  const unsavedCount = dirtyUpdates.length + commentsWithoutDecisions.length;

  const persistSession = useCallback(
    (nextSession: CachedSession) => {
      setSession(nextSession);
      writeSession(cacheKey(nextSession.annotationSet.uuid), nextSession);
    },
    [cacheKey]
  );

  useEffect(() => {
    if (!session?.startedAt || view === 'setup') return;
    const updateElapsed = () => setElapsed(Date.now() - new Date(session.startedAt).getTime());
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(timer);
  }, [session?.startedAt, view]);

  const handleStart = async () => {
    const isUpdatedAtReview = reviewSource === 'updated-at';
    let frames: ReviewFrame[];
    let sourceSet: AnnotationSet;
    try {
      if (isUpdatedAtReview) {
        if (!updatedAtRange || !updatedAtSourceId) return;
        frames = await loadUpdatedAtReviewFrames({
          updatedAtAfter: updatedAtRange.after,
          updatedAtBefore: updatedAtRange.before,
        }).unwrap();
        sourceSet = {
          uuid: updatedAtSourceId,
          icid: 'UPDATED',
          name: `Segmentations updated ${updatedAtRange.after} – ${updatedAtRange.before}`,
          createdAt: new Date().toISOString(),
          userEmail: '',
          numberOfFinishedDicomLabelingJobs: 0,
          numberOfFinishedStudyLabelingJobs: 0,
          totalNumberOfDicomLabelingJobs: 0,
          totalNumberOfStudyLabelingJobs: 0,
        };
      } else {
        if (!selectedSet) return;
        frames = await loadReviewFrames(selectedSet.uuid).unwrap();
        sourceSet = selectedSet;
      }
      if (!frames.length) {
        message.info(
          isUpdatedAtReview
            ? 'No frames have active segmentation labels updated in this date range.'
            : 'This annotation set has no frames with active segmentations.'
        );
        return;
      }

      const cached = readSession(cacheKey(sourceSet.uuid));
      const nextSession: CachedSession = cached
        ? reconcileCachedSession(cached, frames)
        : {
            version: 1,
            annotationSet: {
              uuid: sourceSet.uuid,
              icid: sourceSet.icid,
              name: sourceSet.name,
            },
            sourceType: isUpdatedAtReview ? 'updated-at' : 'annotation-set',
            ...(isUpdatedAtReview
              ? {
                  segmentationUpdatedAtAfter: updatedAtRange?.after,
                  segmentationUpdatedAtBefore: updatedAtRange?.before,
                }
              : {}),
            currentIndex: 0,
            total: frames.length,
            startedAt: new Date().toISOString(),
            choices: {},
            comments: {},
            savedChoices: {},
            savedComments: {},
          };

      nextSession.total = frames.length;
      nextSession.sourceType = isUpdatedAtReview ? 'updated-at' : 'annotation-set';
      nextSession.segmentationUpdatedAtAfter = isUpdatedAtReview
        ? updatedAtRange?.after
        : undefined;
      nextSession.segmentationUpdatedAtBefore = isUpdatedAtReview
        ? updatedAtRange?.before
        : undefined;
      nextSession.currentIndex = Math.min(nextSession.currentIndex, frames.length - 1);
      setActiveSet(sourceSet);
      setReviewFrames(frames);
      setSummaryPage(1);
      persistSession(nextSession);
      setView('review');
    } catch {
      message.error(
        isUpdatedAtReview
          ? 'Could not load review frames for this segmentation update range.'
          : 'Could not load review frames for this annotation set.'
      );
    }
  };

  const moveTo = useCallback(
    (index: number) => {
      if (!session || reviewFrames.length === 0) return;
      if (index >= reviewFrames.length) {
        setView('summary');
        return;
      }
      const boundedIndex = Math.max(0, index);
      persistSession({ ...session, currentIndex: boundedIndex });
      setView('review');
    },
    [persistSession, reviewFrames.length, session]
  );

  const choose = useCallback(
    (decision: FrameReviewDecision) => {
      if (!session || !activeReviewFrame) return;
      const segmentations: SegmentationLabel[] = scopedActiveFrame?.segmentationLabels || [];
      const key = reviewFrameKey(activeReviewFrame);
      const nextSession: CachedSession = {
        ...session,
        choices: {
          ...session.choices,
          [key]: {
            reviewFrameUuid: key,
            dicomId: activeReviewFrame.dicomId,
            dicomIcid: activeReviewFrame.dicomIcid,
            frameIndex: activeReviewFrame.frameIndex,
            decision,
            features:
              segmentations.length > 0
                ? Array.from(
                    new Set(segmentations.map(segmentation => segmentation.segmentationClassValue))
                  ).filter(Boolean)
                : activeReviewFrame.features,
            reviewedAt: new Date().toISOString(),
          },
        },
        currentIndex: Math.min(currentIndex + 1, reviewFrames.length - 1),
      };
      persistSession(nextSession);
      if (currentIndex + 1 >= reviewFrames.length) setView('summary');
    },
    [
      activeReviewFrame,
      currentIndex,
      persistSession,
      reviewFrames.length,
      scopedActiveFrame,
      session,
    ]
  );

  const updateComment = useCallback(
    (comment: string) => {
      if (!session || !activeReviewFrameKey) return;
      persistSession({
        ...session,
        comments: {
          ...(session.comments || {}),
          [activeReviewFrameKey]: comment,
        },
      });
    },
    [activeReviewFrameKey, persistSession, session]
  );

  useEffect(() => {
    if (view !== 'review') return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      const key = event.key.toLowerCase();
      if (event.key === 'ArrowLeft') moveTo(currentIndex - 1);
      if (event.key === 'ArrowRight') moveTo(currentIndex + 1);
      if (key === 's') choose('DISMISSED');
      if (key === 'm') choose('MISSING');
      if (key === 'c') choose('SWAP');
      if (key === 'r') choose('REDO');
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [choose, currentIndex, moveTo, view]);

  const handleSave = async () => {
    if (!session) return;
    if (commentsWithoutDecisions.length > 0) {
      message.warning(
        `${commentsWithoutDecisions.length} comment${commentsWithoutDecisions.length === 1 ? '' : 's'} cannot be saved until a review decision is selected.`
      );
    }
    if (dirtyUpdates.length === 0) return;
    setIsSaving(true);
    const results: PromiseSettledResult<void>[] = [];
    const batchSize = 5;
    for (let index = 0; index < dirtyUpdates.length; index += batchSize) {
      const batch = dirtyUpdates.slice(index, index + batchSize);
      const batchResults = await Promise.allSettled(
        batch.map(update =>
          updateReviewFrame({
            ...(session.sourceType === 'updated-at'
              ? {
                  segmentationUpdatedAtAfter: session.segmentationUpdatedAtAfter,
                  segmentationUpdatedAtBefore: session.segmentationUpdatedAtBefore,
                }
              : { annotationSet: session.annotationSet.uuid }),
            dicomId: update.dicomId,
            frameIndex: update.frameIndex,
            ...(update.comment !== undefined ? { comment: update.comment } : {}),
            ...(update.decision ? { decision: update.decision } : {}),
            ...(update.status ? { status: update.status } : {}),
          }).unwrap()
        )
      );
      results.push(...batchResults);
    }

    const savedChoices = { ...session.savedChoices };
    const savedComments = { ...(session.savedComments || {}) };
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        const update = dirtyUpdates[index];
        if (update.decision) savedChoices[update.reviewFrameUuid] = update.decision;
        if (update.comment !== undefined) savedComments[update.reviewFrameUuid] = update.comment;
      }
    });
    const failures = results.filter(result => result.status === 'rejected').length;
    persistSession({
      ...session,
      savedChoices,
      savedComments,
      lastSavedAt: new Date().toISOString(),
    });
    setIsSaving(false);

    if (failures > 0) {
      message.error(
        `${failures} review${failures === 1 ? '' : 's'} could not be saved and remain cached locally.`
      );
    } else {
      message.success(`${results.length} frame${results.length === 1 ? '' : 's'} saved.`);
    }
  };

  const summaryRows = useMemo(() => {
    if (!session) return [];
    return reviewFrames.map((frame, index): SummaryRow => {
      const key = reviewFrameKey(frame);
      const localChoice = session.choices[key];
      return {
        reviewFrameUuid: key,
        dicomId: frame.dicomId,
        dicomIcid: frame.dicomIcid,
        frameIndex: frame.frameIndex,
        queueIndex: index + 1,
        decision: localChoice?.decision || asFrameReviewDecision(frame.decision),
        features: localChoice?.features.length ? localChoice.features : frame.features,
        reviewedAt: localChoice?.reviewedAt || '',
      };
    });
  }, [reviewFrames, session]);
  const summaryColumns: ColumnsType<SummaryRow> = [
    {
      title: '#',
      dataIndex: 'queueIndex',
      width: 70,
      sorter: (a, b) => a.queueIndex - b.queueIndex,
    },
    { title: 'DICOM', dataIndex: 'dicomIcid' },
    { title: 'Frame', dataIndex: 'frameIndex', width: 90 },
    {
      title: 'Review',
      dataIndex: 'decision',
      width: 150,
      sorter: (a, b) =>
        (a.decision ? decisionLabel(a.decision) : 'Not reviewed').localeCompare(
          b.decision ? decisionLabel(b.decision) : 'Not reviewed'
        ),
      render: (decision?: FrameReviewDecision) =>
        decision ? (
          <Tag color={decisionColor(decision)}>{decisionLabel(decision)}</Tag>
        ) : (
          <Tag>Not reviewed</Tag>
        ),
    },
    {
      title: 'Features',
      dataIndex: 'features',
      render: (features: string[]) => features.join(', ') || '—',
    },
  ];

  if (view === 'setup') {
    return (
      <Page>
        <SetupShell>
          <Eyebrow>Frame quality control</Eyebrow>
          <Title level={1} style={{ margin: 0, color: '#102a43' }}>
            Review frames, one decision at a time
          </Title>
          <Paragraph style={{ maxWidth: 680, marginTop: 12, color: '#52667a', fontSize: 16 }}>
            Choose an annotation set or select segmentation labels by update date. Your queue and
            decisions stay in this browser until you explicitly save them.
          </Paragraph>

          <SetupCard>
            {setsError && (
              <Alert
                type="error"
                showIcon
                message="Annotation sets could not be loaded"
                style={{ marginBottom: 18 }}
              />
            )}
            <FieldLabel htmlFor="annotation-set-select">Annotation set</FieldLabel>
            <Select
              id="annotation-set-select"
              size="large"
              showSearch
              allowClear
              loading={setsAreLoading}
              value={selectedSetId}
              onChange={value => {
                setSelectedSetId(value);
                if (value) setReviewSource('annotation-set');
              }}
              placeholder="Choose a set to review"
              style={{ width: '100%' }}
              optionFilterProp="label"
              options={annotationSets.map(annotationSet => ({
                value: annotationSet.uuid,
                label: `${annotationSet.name || annotationSet.icid} · ${annotationSet.totalNumberOfStudyLabelingJobs} studies`,
              }))}
              notFoundContent={setsAreLoading ? <Skeleton.Input active block /> : 'No sets found'}
            />
            <SetupHint>
              Every frame with an active segmentation in the selected set is available. Review rows
              are created only when you save. Skip, Missing, Change class, and Redo mirror the
              offline review tool.
            </SetupHint>

            <Divider plain>or filter by segmentation update date</Divider>
            <FieldLabel>Segmentation label updated at</FieldLabel>
            <DatePicker.RangePicker
              aria-label="Segmentation label updated-at range"
              style={{ width: '100%' }}
              onChange={(_dates, dateStrings) => {
                const after = dateStrings[0];
                const before = dateStrings[1];
                if (after && before) {
                  setUpdatedAtRange({ after, before });
                  setReviewSource('updated-at');
                } else {
                  setUpdatedAtRange(undefined);
                  if (reviewSource === 'updated-at') setReviewSource('annotation-set');
                }
              }}
            />
            <SetupHint>
              A frame is selected when at least one active segmentation label was updated in this
              inclusive date range. The review view then shows all active segmentations on every
              selected frame.
            </SetupHint>

            {resumableSession && (
              <Alert
                type="info"
                showIcon
                message={`A local review is available with ${Object.keys(resumableSession.choices).length} completed frame${Object.keys(resumableSession.choices).length === 1 ? '' : 's'}.`}
                style={{ marginTop: 18 }}
              />
            )}
            <Button
              type="primary"
              size="large"
              block
              icon={<PlayCircleOutlined />}
              disabled={reviewSource === 'updated-at' ? !updatedAtRange : !selectedSetId}
              loading={reviewSource === 'updated-at' ? updatedAtFramesAreLoading : framesAreLoading}
              onClick={handleStart}
              style={{ marginTop: 22, height: 46, fontWeight: 700 }}
            >
              {resumableSession
                ? 'Resume review'
                : reviewSource === 'updated-at'
                  ? 'Start date-filtered review'
                  : 'Start review'}
            </Button>
          </SetupCard>
        </SetupShell>
      </Page>
    );
  }

  if (!session || !activeSet) return null;

  if (view === 'summary') {
    return (
      <Page>
        <Workspace>
          <SummaryCard>
            <SummaryHeader>
              <div>
                <Eyebrow>Review summary</Eyebrow>
                <Title level={2} style={{ margin: 0 }}>
                  {activeSet.name || activeSet.icid}
                </Title>
                <Paragraph style={{ margin: '8px 0 0', color: '#667085' }}>
                  {reviewedCount} of {total} frames reviewed · {unsavedCount} unsaved changes
                </Paragraph>
              </div>
              <Space wrap>
                <Button onClick={() => setView('setup')}>Change set</Button>
                <Button icon={<ArrowLeftOutlined />} onClick={() => moveTo(currentIndex)}>
                  Return to frame
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={isSaving}
                  disabled={unsavedCount === 0}
                  onClick={handleSave}
                >
                  Save {unsavedCount || ''} changes
                </Button>
              </Space>
            </SummaryHeader>
            {summaryRows.length > 0 ? (
              <Table<SummaryRow>
                rowKey="reviewFrameUuid"
                columns={summaryColumns}
                dataSource={summaryRows}
                pagination={{
                  current: summaryPage,
                  pageSize: summaryPageSize,
                  pageSizeOptions: [20, 50, 100],
                  showSizeChanger: true,
                  hideOnSinglePage: false,
                  onChange: (page, pageSize) => {
                    setSummaryPage(pageSize === summaryPageSize ? page : 1);
                    setSummaryPageSize(pageSize);
                  },
                }}
                onRow={row => ({
                  onClick: () => {
                    const index = reviewFrames.findIndex(
                      frame => reviewFrameKey(frame) === row.reviewFrameUuid
                    );
                    if (index >= 0) moveTo(index);
                  },
                  style: { cursor: 'pointer' },
                })}
              />
            ) : (
              <Empty description="No frames have been reviewed yet" style={{ padding: 48 }} />
            )}
          </SummaryCard>
        </Workspace>
      </Page>
    );
  }

  return (
    <Page>
      <Workspace>
        <Toolbar>
          <ToolbarTitle>
            <strong>{activeSet.name || activeSet.icid}</strong>
            <span>
              Frame {currentIndex + 1} of {total} · {reviewedCount} reviewed
            </span>
          </ToolbarTitle>
          <ProgressBlock>
            <Progress percent={progress} strokeColor="#2896f6" />
          </ProgressBlock>
          <Space wrap>
            <Button onClick={() => setView('setup')}>Change set</Button>
            <Button icon={<UnorderedListOutlined />} onClick={() => setView('summary')}>
              See all
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isSaving}
              disabled={unsavedCount === 0}
              onClick={handleSave}
            >
              Save {unsavedCount || ''}
            </Button>
          </Space>
        </Toolbar>

        {activeReviewFrame && (
          <FrameMeta>
            <DatabaseOutlined />
            <span>
              DICOM <strong>{activeReviewFrame.dicomIcid}</strong>
            </span>
            <span>
              Frame <strong>{activeReviewFrame.frameIndex}</strong>
            </span>
            <span>
              Study <strong>{activeReviewFrame.studyIcid}</strong>
            </span>
            {persistedDecision && !activeChoice && (
              <Tag color={decisionColor(persistedDecision)}>Saved review: {persistedDecision}</Tag>
            )}
            {activeChoice && (
              <Tag color={decisionColor(activeChoice.decision)}>
                {localChoiceIsSaved ? 'Saved' : 'Unsaved'} review: {activeChoice.decision}
              </Tag>
            )}
            <MetaStatus>
              <Timer>
                Total time:<strong>{formatDuration(elapsed)}</strong>
              </Timer>
              <CacheNotice>
                <CloudOutlined /> {unsavedCount} unsaved · cached in this browser
              </CacheNotice>
            </MetaStatus>
          </FrameMeta>
        )}

        <ActionDock>
          <ActionGroup>
            <Button
              icon={<ArrowLeftOutlined />}
              disabled={currentIndex === 0}
              onClick={() => moveTo(currentIndex - 1)}
            >
              Previous
            </Button>
            <Button disabled={currentIndex + 1 >= total} onClick={() => moveTo(currentIndex + 1)}>
              Next <ArrowRightOutlined />
            </Button>
          </ActionGroup>
          <ActionGroup>
            <Button
              aria-pressed={effectiveDecision === 'DISMISSED'}
              style={selectedDecisionStyle('DISMISSED', effectiveDecision === 'DISMISSED')}
              onClick={() => choose('DISMISSED')}
            >
              Skip / Dismiss <kbd>S</kbd>
            </Button>
            <Button
              aria-pressed={effectiveDecision === 'MISSING'}
              style={selectedDecisionStyle('MISSING', effectiveDecision === 'MISSING')}
              onClick={() => choose('MISSING')}
            >
              Missing <kbd>M</kbd>
            </Button>
            <Button
              aria-pressed={effectiveDecision === 'SWAP'}
              style={selectedDecisionStyle('SWAP', effectiveDecision === 'SWAP')}
              onClick={() => choose('SWAP')}
            >
              Change class / Swap <kbd>C</kbd>
            </Button>
            <Button
              aria-pressed={effectiveDecision === 'REDO'}
              style={selectedDecisionStyle('REDO', effectiveDecision === 'REDO')}
              onClick={() => choose('REDO')}
            >
              Redo <kbd>R</kbd>
            </Button>
          </ActionGroup>
          {activeReviewFrame && (
            <ReviewComment>
              <Input
                id="frame-review-comment"
                aria-label="Review comment"
                value={activeComment}
                maxLength={2000}
                placeholder="Review comment"
                status={activeComment.trim() && !effectiveDecision ? 'warning' : undefined}
                onChange={event => updateComment(event.target.value)}
                onKeyDown={event => event.stopPropagation()}
              />
              {activeComment.trim() && !effectiveDecision && (
                <span role="alert">Select a review decision before this message can be saved.</span>
              )}
            </ReviewComment>
          )}
        </ActionDock>

        {previewIsPending && (
          <PreviewGrid>
            <Skeleton.Image active style={{ width: '100%', height: 440 }} />
            <Skeleton.Image active style={{ width: '100%', height: 440 }} />
          </PreviewGrid>
        )}
        {previewError && !scopedActiveFrame && (
          <Alert
            type="error"
            showIcon
            message="The frame preview could not be loaded"
            description="You can navigate to another frame and return to retry."
          />
        )}
        {scopedActiveFrame && (
          <PreviewGrid key={activeReviewFrameKey}>
            <FramePreview frame={scopedActiveFrame} />
            <FramePreview frame={scopedActiveFrame} overlay />
          </PreviewGrid>
        )}
        {!previewIsFetching && !previewError && !scopedActiveFrame && (
          <Empty description="No frame image or segmentation labels were found" />
        )}
      </Workspace>
    </Page>
  );
};

export default FrameReview;
